import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type AiSuggestionView,
  type ReschedulePlanOutput as ReschedulePlan,
  ReschedulePlanOutput,
} from '@apm/contracts';
import { errorMessage } from '../../lib/error-message';
import { useTrackedAiRun } from '../ai/use-ai-run';
import { ganttApi, ganttKeys } from './api';

/**
 * Một lượt "Xếp lịch bằng AI" — APM-1313b.
 *
 * ## Vì sao tách khỏi `ReschedulePanel`
 *
 * Nút bấm nay nằm ở thanh công cụ (cạnh "Việc mới"), còn bảng duyệt từng nước
 * đi vẫn ở dưới Gantt — hai chỗ trong hai cây component khác nhau, nhưng phải
 * là **một** lượt chạy: bấm ở trên thì kết quả hiện ở dưới, và lúc đang chạy
 * thì cả hai chỗ cùng biết. Giữ state trong panel rồi dựng thêm một nút ở
 * ngoài là dựng hai lượt chạy song song mà giao diện không nói ra.
 */
export function useReschedule(projectId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const track = useTrackedAiRun();

  const [suggestion, setSuggestion] = useState<AiSuggestionView | null>(null);
  const [chosen, setChosen] = useState<ReadonlySet<number>>(new Set());

  const plan: ReschedulePlan | null = (() => {
    if (suggestion === null) return null;
    const parsed = ReschedulePlanOutput.safeParse(suggestion.content);
    return parsed.success ? parsed.data : null;
  })();

  const ask = useMutation({
    mutationFn: () =>
      track(
        {
          skill: 'reschedule',
          label: t('reschedule.ask'),
          describe: (result: AiSuggestionView) => {
            const parsed = ReschedulePlanOutput.safeParse(result.content);
            if (!parsed.success) return t('reschedule.empty');
            return parsed.data.moves.length === 0
              ? t('reschedule.noPlan')
              : t('reschedule.movesFound', { count: parsed.data.moves.length });
          },
        },
        (runId) => ganttApi.askReschedule(projectId, t('reschedule.prompt'), runId),
      ),
    onSuccess: (result) => {
      setSuggestion(result);
      const parsed = ReschedulePlanOutput.safeParse(result.content);
      // Tích sẵn tất cả: phương án đã qua bộ kiểm năng lực rồi, nên mặc định
      // "đồng ý cả gói" là mặc định đúng — người dùng bỏ tích thứ họ không muốn.
      setChosen(new Set(parsed.success ? parsed.data.moves.map((_, index) => index) : []));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const apply = useMutation({
    mutationFn: () =>
      ganttApi.applyReschedule(
        projectId,
        suggestion!.id,
        [...chosen].sort((a, b) => a - b),
      ),
    onSuccess: async () => {
      setSuggestion(null);
      setChosen(new Set());
      await queryClient.invalidateQueries({ queryKey: ganttKeys.view(projectId) });
      await queryClient.invalidateQueries({ queryKey: ganttKeys.impact(projectId) });
      toast.success(t('reschedule.applied'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  return { suggestion, setSuggestion, chosen, setChosen, plan, ask, apply };
}
